<template>
  <BaseDataIterator
    ref="dataList"
    :service="fetchContacts"
    :filter-options="filterOptions"
    :save-query-action="saveQueryAction"
    :load-queries-action="loadQueriesAction"
    :export-action="exportContacts"
  >
    <template #before-default="{ queryParams }">
      <div
        v-if="queryParams.filter && Object.keys(queryParams.filter).length > 0"
        class="px-4 py-3 flex gap-2"
      >
        <AddToListAction :account-id="accountId" :query="{ filter: queryParams.filter }" />
        <AddTagsAction :account-id="accountId" :query="{ filter: queryParams.filter }" />
      </div>
    </template>
    <template #default="{ data }">
      <BaseDataTable :data="data">
        <template #header>
          <ContactListHeader />
        </template>

        <template #default="{ item }">
          <ContactListItem
            :contact="item"
            @contact-deleted="refreshList"
            @contact-updated="refreshList"
            :key="item.id"
          />
        </template>
      </BaseDataTable>
    </template>
  </BaseDataIterator>
</template>

<script>
import { snakeCase } from 'lodash-es';
import { accountContactsClient, accountContactSavedQueriesClient } from '@/api/clients';
import contactFilters from '@/filters/contact';
import ContactListHeader from './ContactListHeader.vue';
import ContactListItem from './ContactListItem.vue';
import AddToListAction from './AddToListAction.vue';
import AddTagsAction from './AddTagsAction.vue';

export default {
  components: {
    ContactListHeader,
    ContactListItem,
    AddToListAction,
    AddTagsAction,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filterOptions: contactFilters(this.accountId),
    };
  },
  computed: {
    account() {
      return this.$store.state.entities.accounts.byId[this.accountId];
    },
    exportFilename() {
      return snakeCase(`${this?.account?.name || 'account'}_contacts`);
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.refreshList();
      },
    },
  },
  methods: {
    saveQueryAction(data) {
      return accountContactSavedQueriesClient.store(this.accountId, data);
    },
    loadQueriesAction() {
      return accountContactSavedQueriesClient.list(this.accountId);
    },
    resetList() {
      this.$refs.dataList.resetList();
    },
    refreshList() {
      const { execute } = this.$refs?.dataList?.$refs?.baseQueryComponent || {};

      if (typeof execute !== 'function') {
        return;
      }

      execute();
    },
    async fetchContacts(params = {}) {
      const include = [
        'primaryEmailAddress',
        'primaryPhoneNumber',
        ...(this.$membershipHasPermissionTo('read contact requests')
          ? ['contactRequestsCount']
          : []),
        ...(this.$membershipHasPermissionTo('read acc orders') ? ['accOrdersCount'] : []),
        'user',
      ];
      try {
        const response = await accountContactsClient.list(this.accountId, {
          ...params,
          include,
        });

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async exportContacts(query = {}) {
      try {
        const response = await accountContactsClient.list(
          this.accountId,
          {
            ...query,
            include: 'primaryEmailAddress,primaryPhoneNumber,contactRequestsCount,accOrdersCount',
            export: true,
          },
          {
            responseType: 'blob',
          }
        );

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
